import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

import {
  useCanvasDimensions,
  useResponsiveConfig,
  useTooltipState,
  useWordCloudConfig,
  useWordCloudRenderer,
} from 'hooks/wordcloud';

import { ScreenReaderContent } from './ScreenReaderContent';
import { WordCloudTooltip } from './WordCloudTooltip';

const styles = {
  container: ({ palette }, containerHeight) => ({
    height: containerHeight,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: palette.colors.lightPale,
    mt: 4,
    borderRadius: 3,
    p: 3,
    position: 'relative',
  }),
  canvas: {
    '&:focus-visible': {
      outline: 'none',
    },
  },
};

export const WordCloud = ({ words = [] }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const responsiveConfig = useResponsiveConfig(words.length);
  const dimensions = useCanvasDimensions(containerRef, canvasRef, responsiveConfig.containerHeight, words.length);
  const { tooltip, handleWordHover, clearTooltip } = useTooltipState(containerRef);

  const wordcloudConfig = useWordCloudConfig({
    words,
    responsiveConfig,
    canvasDimensions: dimensions,
    onWordHover: handleWordHover,
  });

  useWordCloudRenderer({
    canvasRef,
    config: wordcloudConfig,
    words,
  });

  if (!words.length) {
    return (
      <Box sx={(theme) => styles.container(theme, 100)}>
        <Typography variant="body1">No data available for word cloud</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      aria-label="Word cloud visualization"
      role="img"
      tabIndex="0"
      onBlur={clearTooltip}
      sx={(theme) => styles.container(theme, responsiveConfig.containerHeight)}
    >
      {/* Canvas for rendering the wordcloud */}
      <Box component="canvas" ref={canvasRef} aria-hidden="true" tabIndex="-1" sx={styles.canvas} />

      {/* Screen reader accessible content */}
      <ScreenReaderContent words={words} />

      {/* Tooltip for displaying word versions */}
      <WordCloudTooltip versions={tooltip?.versions} anchorEl={tooltip?.anchorEl} />
    </Box>
  );
};

WordCloud.propTypes = {
  words: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      versions: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
};
